/* eslint-disable -- Line 3 merge conflict false positive; file has no conflict markers */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Check, Search } from 'lucide-react';
import { ingestionService } from '../../../../components/api/ingestionService';
import {
  redisConfigurationService,
  type RedisConfiguration,
} from '../../../../components/api/redisConfigurationService';
import {
  dataVaultService,
  type DataVault,
} from '../../../../components/api/dataVaultService';

export interface ScheduleFormValues {
  id?: string;
  name: string;
  entity: string;
  destination: string;
  strategy: string;
  schedule: string;
  enabled: boolean;
  /** When strategy is Incremental: watermark column and primary key for deduplication */
  strategyOptions?: { watermarkColumn?: string; primaryKey?: string };
  /** Schedule configuration (saved to API) */
  frequency?: string;
  runAt?: string;
  /** Selected destination target id (KB / Vector store / Cache / Data vault) */
  destinationId?: string;
  entityName?: string;
  destinationName?: string;
}

interface AddEditScheduleModalProps {
  open: boolean;
  onClose: () => void;
  editSchedule: ScheduleFormValues | null;
  onSubmit: (data: ScheduleFormValues) => void;
}

const Toggle: React.FC<{ on: boolean; onChange: () => void; className?: string }> = ({
  on,
  onChange,
  className = '',
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onChange}
    className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors flex-shrink-0 ${
      on ? 'bg-indigo-600' : 'bg-white/20'
    } ${className}`}
  >
    <span
      className={`absolute top-1 w-3.5 h-3.5 rounded-full bg-white transition-transform ${
        on ? 'left-[18px]' : 'left-1'
      }`}
    />
  </button>
);

/** Reusable filter input — black theme, minimal */
const ListFilter: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
}> = ({ value, onChange, placeholder, className = '' }) => (
  <div className={`relative ${className}`}>
    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600 pointer-events-none" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-9 pr-9 py-2 text-sm rounded-lg bg-zinc-800/80 text-white placeholder:text-zinc-500 outline-none focus:bg-zinc-700/80 focus:ring-1 focus:ring-white/20 transition-colors"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-white rounded transition-colors"
        aria-label="Clear filter"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

/** Section: title only, no card/border */
const Section: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({
  title,
  children,
  className = '',
}) => (
  <div className={className}>
    <h3 className="text-[11px] font-medium uppercase tracking-widest text-zinc-500 mb-4">{title}</h3>
    {children}
  </div>
);

const consistencyOptions = [
  { id: 'Incremental', label: 'Incremental', desc: 'Fetch only rows newer than last sync via watermark. Most efficient.' },
  { id: 'Upsert', label: 'Upsert', desc: 'Insert new, update changed by primary key. Prevents duplicates.' },
  { id: 'Full Refresh', label: 'Full Refresh', desc: 'Replace all data on every run. Guaranteed consistency.' },
  { id: 'Append Only', label: 'Append Only', desc: 'Only add new records. For immutable data and event logs.' },
];

const destinationOptions = [
  { id: 'Knowledge Base', label: '📚 Knowledge Base', desc: 'Add to an existing KB for RAG and AI agent retrieval' },
  { id: 'Vector DB', label: '🔮 Vector DB', desc: 'Embed and index directly (Pinecone, pgvector, Qdrant)' },
  { id: 'Cache', label: '⚡ Cache (Redis)', desc: 'Fast key-value store for frequently queried data' },
  { id: 'Directory', label: '📁 Directory / S3', desc: 'Write files to a local path or S3 bucket' },
];

/** Fallback when API returns empty for Destination > Knowledge Base */
const fallbackKbList = [
  { id: 'kb-1', name: 'No knowledge bases', meta: 'Create a KB in Knowledge Base section', status: 'idle' as const },
];

type EntityCard = { id: string; label: string; meta: string; tags: string[] };

const AddEditScheduleModal: React.FC<AddEditScheduleModalProps> = ({
  open,
  onClose,
  editSchedule,
  onSubmit,
}) => {
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [selectedConsistency, setSelectedConsistency] = useState<string | null>(null);
  const [selectedDestination, setSelectedDestination] = useState<string | null>(null);
  const [selectedKbId, setSelectedKbId] = useState<string | null>(null);
  const [selectedVectorDbId, setSelectedVectorDbId] = useState<string | null>(null);
  const [selectedCacheId, setSelectedCacheId] = useState<string | null>(null);
  const [selectedDirectoryId, setSelectedDirectoryId] = useState<string | null>(null);
  const [showWmSub, setShowWmSub] = useState(false);
  const [watermarkColumn, setWatermarkColumn] = useState('updated_at');
  const [primaryKey, setPrimaryKey] = useState('id');
  const [scheduleName, setScheduleName] = useState('');
  const [frequency, setFrequency] = useState('Daily');
  const [runAt, setRunAt] = useState('02:00');
  const [activeToggle, setActiveToggle] = useState(true);

  const [dbEntities, setDbEntities] = useState<EntityCard[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);

  const [kbOptions, setKbOptions] = useState<{ id: string; name: string; meta: string; status: 'active' | 'idle' }[]>([]);
  const [kbOptionsLoading, setKbOptionsLoading] = useState(false);
  const [kbOptionsError, setKbOptionsError] = useState<string | null>(null);

  const [vectorStoreOptions, setVectorStoreOptions] = useState<{ id: string; name: string; meta: string }[]>([]);
  const [vectorStoreLoading, setVectorStoreLoading] = useState(false);
  const [vectorStoreError, setVectorStoreError] = useState<string | null>(null);

  const [cacheOptions, setCacheOptions] = useState<{ id: string; name: string; meta: string }[]>([]);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheError, setCacheError] = useState<string | null>(null);

  const [directoryOptions, setDirectoryOptions] = useState<{ id: string; name: string; meta: string }[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);

  const [entityFilter, setEntityFilter] = useState('');
  const [kbFilter, setKbFilter] = useState('');
  const [vectorDbFilter, setVectorDbFilter] = useState('');
  const [cacheFilter, setCacheFilter] = useState('');
  const [directoryFilter, setDirectoryFilter] = useState('');
  const filtersClearedOnOpen = useRef(false);

  const filteredEntities = useMemo(() => {
    if (!entityFilter.trim()) return dbEntities;
    const q = entityFilter.toLowerCase().trim();
    return dbEntities.filter(
      (e) =>
        e.label.toLowerCase().includes(q) ||
        e.meta.toLowerCase().includes(q) ||
        e.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [dbEntities, entityFilter]);

  const filteredKbOptions = useMemo(() => {
    if (!kbFilter.trim()) return kbOptions;
    const q = kbFilter.toLowerCase().trim();
    return kbOptions.filter(
      (k) => k.name.toLowerCase().includes(q) || k.meta.toLowerCase().includes(q)
    );
  }, [kbOptions, kbFilter]);

  const filteredVectorStores = useMemo(() => {
    if (!vectorDbFilter.trim()) return vectorStoreOptions;
    const q = vectorDbFilter.toLowerCase().trim();
    return vectorStoreOptions.filter(
      (v) => v.name.toLowerCase().includes(q) || v.meta.toLowerCase().includes(q)
    );
  }, [vectorStoreOptions, vectorDbFilter]);

  const filteredCacheOptions = useMemo(() => {
    if (!cacheFilter.trim()) return cacheOptions;
    const q = cacheFilter.toLowerCase().trim();
    return cacheOptions.filter(
      (c) => c.name.toLowerCase().includes(q) || c.meta.toLowerCase().includes(q)
    );
  }, [cacheOptions, cacheFilter]);

  const filteredDirectoryOptions = useMemo(() => {
    if (!directoryFilter.trim()) return directoryOptions;
    const q = directoryFilter.toLowerCase().trim();
    return directoryOptions.filter(
      (d) => d.name.toLowerCase().includes(q) || d.meta.toLowerCase().includes(q)
    );
  }, [directoryOptions, directoryFilter]);

  const hasDestinationTarget =
    (selectedDestination === 'Knowledge Base' && selectedKbId != null) ||
    (selectedDestination === 'Vector DB' && selectedVectorDbId != null) ||
    (selectedDestination === 'Cache' && selectedCacheId != null) ||
    (selectedDestination === 'Directory' && selectedDirectoryId != null);

  const allRequiredFilled =
    scheduleName.trim() !== '' &&
    selectedEntity != null &&
    selectedConsistency != null &&
    selectedDestination != null &&
    hasDestinationTarget;

  useEffect(() => {
    if (!open) return;
    const fetchEntities = async () => {
      setEntitiesLoading(true);
      setEntitiesError(null);
      try {
        const list = await ingestionService.scheduler.getEntities();
        const items = Array.isArray(list) ? list : [];
        const toCard = (ds: any): EntityCard => ({
          id: ds.id,
          label: ds.connectionName || ds.id,
          meta: [ds.description, ds.sourceType].filter(Boolean).join(' · ') || '—',
          tags: typeof ds.tags === 'string' ? (ds.tags ? ds.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : []) : [],
        });
        setDbEntities(items.map(toCard));
      } catch (err) {
        console.error('Failed to load entities (DataSourceConfiguration):', err);
        setEntitiesError('Failed to load entities. Please try again.');
        setDbEntities([]);
      } finally {
        setEntitiesLoading(false);
      }
    };
    const fetchKbOptions = async () => {
      setKbOptionsLoading(true);
      setKbOptionsError(null);
      try {
        const raw = await ingestionService.scheduler.getKnowledgeBasesForDestination();
        const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && Array.isArray((raw as any).data) ? (raw as any).data : []);
        const items = Array.isArray(list) ? list : [];
        if (items.length === 0) {
          setKbOptions(fallbackKbList);
        } else {
          setKbOptions(
            items.map((kb: Record<string, unknown>) => {
              const id = (kb.id ?? kb.Id) as string;
              const title = (kb.title ?? kb.Title) as string;
              const workspaceTitle = (kb.workspaceTitle ?? kb.WorkspaceTitle) as string | undefined;
              return {
                id: id ?? '',
                name: title || id || 'Untitled',
                meta: workspaceTitle ? `Workspace: ${workspaceTitle}` : '—',
                status: 'active' as const,
              };
            })
          );
        }
      } catch (err) {
        console.error('Failed to load knowledge bases for destination:', err);
        setKbOptionsError('Failed to load knowledge bases.');
        setKbOptions(fallbackKbList);
      } finally {
        setKbOptionsLoading(false);
      }
    };
    const fetchVectorStores = async () => {
      setVectorStoreLoading(true);
      setVectorStoreError(null);
      try {
        const raw = await ingestionService.getVectorStores();
        const list = Array.isArray(raw) ? raw : [];
        setVectorStoreOptions(
          list.map((vs: Record<string, unknown>) => {
            const id = (vs.id ?? vs.Id) as string;
            const name = (vs.description ?? vs.collectionName ?? vs.provider ?? id ?? 'Vector Store') as string;
            const meta = [vs.provider, vs.region].filter(Boolean).join(' · ') || '—';
            return { id: String(id), name: String(name), meta };
          })
        );
      } catch (err) {
        console.error('Failed to load vector stores for destination:', err);
        setVectorStoreError('Failed to load vector stores.');
        setVectorStoreOptions([]);
      } finally {
        setVectorStoreLoading(false);
      }
    };
    const fetchCacheOptions = async () => {
      setCacheLoading(true);
      setCacheError(null);
      try {
        const list = await redisConfigurationService.getList();
        const items = Array.isArray(list) ? list : [];
        setCacheOptions(
          items.map((c: RedisConfiguration) => {
            const id = c.id ?? '';
            const name = (c.name ?? c.description ?? id) || 'Cache';
            const meta = c.connectionString ?? (c.host != null && c.port != null ? `${c.host}:${c.port}` : null) ?? '—';
            return { id: String(id), name: String(name), meta };
          })
        );
      } catch (err) {
        console.error('Failed to load cache (Redis) for destination:', err);
        setCacheError('Failed to load cache configurations.');
        setCacheOptions([]);
      } finally {
        setCacheLoading(false);
      }
    };
    const fetchDirectoryOptions = async () => {
      setDirectoryLoading(true);
      setDirectoryError(null);
      try {
        const list = await dataVaultService.getList();
        const items = Array.isArray(list) ? list : [];
        setDirectoryOptions(
          items.map((d: DataVault) => {
            const id = d.id ?? '';
            const name = (d.name ?? d.description ?? id) || 'Data Vault';
            const meta = (d.type ?? d.description) ?? '—';
            return { id: String(id), name: String(name), meta: String(meta) };
          })
        );
      } catch (err) {
        console.error('Failed to load data vaults for destination:', err);
        setDirectoryError('Failed to load data vaults.');
        setDirectoryOptions([]);
      } finally {
        setDirectoryLoading(false);
      }
    };
    fetchEntities();
    fetchKbOptions();
    fetchVectorStores();
    fetchCacheOptions();
    fetchDirectoryOptions();
  }, [open]);

  useEffect(() => {
    if (!open) {
      filtersClearedOnOpen.current = false;
      return;
    }
    if (!filtersClearedOnOpen.current) {
      filtersClearedOnOpen.current = true;
      setEntityFilter('');
      setKbFilter('');
      setVectorDbFilter('');
      setCacheFilter('');
      setDirectoryFilter('');
    }
    if (editSchedule) {
      setScheduleName(editSchedule.name);
      setSelectedEntity(editSchedule.entity);
      setSelectedConsistency(editSchedule.strategy);
      setSelectedDestination(editSchedule.destination);
      setActiveToggle(editSchedule.enabled);
      setWatermarkColumn(editSchedule.strategyOptions?.watermarkColumn ?? 'updated_at');
      setPrimaryKey(editSchedule.strategyOptions?.primaryKey ?? 'id');
      if (editSchedule.frequency) setFrequency(editSchedule.frequency);
      if (editSchedule.runAt) setRunAt(editSchedule.runAt);
      const [freq, time] = (editSchedule.schedule || '').split(' ');
      if (time && !editSchedule.runAt) setRunAt(time);
      if (freq && !editSchedule.frequency) setFrequency(freq);
      if (editSchedule.destinationId) {
        if (editSchedule.destination === 'Knowledge Base') setSelectedKbId(editSchedule.destinationId);
        else if (editSchedule.destination === 'Vector DB') setSelectedVectorDbId(editSchedule.destinationId);
        else if (editSchedule.destination === 'Cache') setSelectedCacheId(editSchedule.destinationId);
        else if (editSchedule.destination === 'Directory') setSelectedDirectoryId(editSchedule.destinationId);
      } else {
        setSelectedKbId(null);
        setSelectedVectorDbId(null);
        setSelectedCacheId(null);
        setSelectedDirectoryId(null);
      }
      setShowWmSub(editSchedule.strategy === 'Incremental');
    } else {
      setScheduleName('');
      setSelectedEntity(null);
      setSelectedConsistency(null);
      setSelectedDestination(null);
      setSelectedKbId(null);
      setSelectedVectorDbId(null);
      setSelectedCacheId(null);
      setSelectedDirectoryId(null);
      setShowWmSub(false);
      setWatermarkColumn('updated_at');
      setPrimaryKey('id');
      setFrequency('Daily');
      setRunAt('02:00');
      setActiveToggle(true);
    }
  }, [open, editSchedule]);

  const handleSubmit = () => {
    if (!allRequiredFilled) return;
    const strategy = selectedConsistency || (editSchedule?.strategy ?? 'Incremental');
    const destId = selectedKbId ?? selectedVectorDbId ?? selectedCacheId ?? selectedDirectoryId ?? editSchedule?.destinationId;
    const entityNameDisplay = selectedEntity ? dbEntities.find((e) => e.id === selectedEntity)?.label : editSchedule?.entityName;
    const destNameDisplay =
      selectedDestination === 'Knowledge Base'
        ? kbOptions.find((k) => k.id === selectedKbId)?.name
        : selectedDestination === 'Vector DB'
          ? vectorStoreOptions.find((v) => v.id === selectedVectorDbId)?.name
          : selectedDestination === 'Cache'
            ? cacheOptions.find((c) => c.id === selectedCacheId)?.name
            : selectedDestination === 'Directory'
              ? directoryOptions.find((d) => d.id === selectedDirectoryId)?.name
              : editSchedule?.destinationName;
    onSubmit({
      id: editSchedule?.id,
      name: scheduleName || (editSchedule?.name ?? 'New Schedule'),
      entity: selectedEntity || (editSchedule?.entity ?? 'entity'),
      destination: selectedDestination || (editSchedule?.destination ?? 'Destination'),
      strategy,
      schedule: `${frequency} ${runAt}`,
      enabled: activeToggle,
      strategyOptions:
        strategy === 'Incremental' ? { watermarkColumn, primaryKey } : undefined,
      frequency,
      runAt,
      destinationId: destId ?? undefined,
      entityName: entityNameDisplay,
      destinationName: destNameDisplay,
    });
    onClose();
  };

  if (!open) return null;

  const title = editSchedule ? 'Edit Schedule' : 'Add Schedule';
  const sub = editSchedule ? editSchedule.name : 'Configure a new automated data sync job';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} aria-hidden />
      <div
        className="relative w-[85vw] max-w-[1100px] max-h-[90vh] flex flex-col rounded-2xl bg-zinc-950 overflow-hidden border border-zinc-800/80"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-8 py-5 flex-shrink-0 bg-zinc-900/50">
          <div>
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <p className="text-sm text-zinc-500 mt-0.5">{sub}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-zinc-500 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="px-8 pb-8">
            {/* Job name + Schedule in one row on large screens */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:gap-8 mb-10">
              <div className="flex-1 min-w-0">
                <label className="text-[11px] font-medium uppercase tracking-widest text-zinc-500 block mb-2">
                  Job name <span className="text-amber-400">*</span>
                </label>
                <input
                  type="text"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  placeholder="e.g. Daily Customer Sync"
                  className="w-full max-w-md px-4 py-2.5 text-sm rounded-lg bg-zinc-800/80 text-white placeholder:text-zinc-500 outline-none focus:bg-zinc-700/80 focus:ring-1 focus:ring-white/20 transition-colors"
                />
                {scheduleName.trim() === '' && (
                  <p className="text-xs text-amber-400 mt-1.5">Name is required</p>
                )}
              </div>
              <div className="flex flex-wrap items-end gap-6 mt-6 lg:mt-0">
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-widest text-zinc-500 block mb-2">Frequency</label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className="px-4 py-2.5 text-sm rounded-lg bg-zinc-800/80 text-white outline-none focus:bg-zinc-700/80 focus:ring-1 focus:ring-white/20 min-w-[140px]"
                  >
                    <option>Every 15 min</option>
                    <option>Every 30 min</option>
                    <option>Hourly</option>
                    <option>Every 6 hours</option>
                    <option>Daily</option>
                    <option>Weekly</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-widest text-zinc-500 block mb-2">Run at (UTC)</label>
                  <input
                    type="time"
                    value={runAt}
                    onChange={(e) => setRunAt(e.target.value)}
                    className="px-4 py-2.5 text-sm rounded-lg bg-zinc-800/80 text-white outline-none focus:bg-zinc-700/80 focus:ring-1 focus:ring-white/20"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-medium uppercase tracking-widest text-zinc-500">Active</span>
                  <Toggle on={activeToggle} onChange={() => setActiveToggle(!activeToggle)} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-12 xl:gap-16">
              <div className="space-y-10">
                <Section title="Data source">
                <p className="text-xs text-zinc-500 mb-3">Database tables &amp; queries</p>
              {entitiesError && (
                <p className="text-xs text-amber-500 mb-2">{entitiesError}</p>
              )}
              {!entitiesLoading && selectedEntity == null && (
                <p className="text-xs text-amber-500 mb-2">Select an entity (required)</p>
              )}
              {entitiesLoading ? (
                <p className="text-xs text-zinc-500 mb-3">Loading entities…</p>
              ) : (
                <div className="space-y-3">
                  <ListFilter
                    value={entityFilter}
                    onChange={setEntityFilter}
                    placeholder="Search entities…"
                  />
                  <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                    {filteredEntities.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setSelectedEntity(e.id)}
                        className={`rounded-lg p-3 text-left transition-colors cursor-pointer ${
                          selectedEntity === e.id ? 'bg-indigo-500/20 text-white ring-1 ring-indigo-500/30' : 'bg-zinc-800/70 hover:bg-zinc-700/80 text-zinc-300'
                        }`}
                      >
                        <div className="text-xs font-medium truncate text-inherit">{e.label}</div>
                        <div className="text-[11px] text-zinc-500 truncate mt-0.5">{e.meta}</div>
                        {e.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {e.tags.map((t) => (
                              <span key={t} className="text-[10px] text-zinc-500">{t}</span>
                            ))}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {filteredEntities.length === 0 && dbEntities.length > 0 && (
                    <p className="text-xs text-zinc-500 py-2">No entities match your filter.</p>
                  )}
                </div>
              )}
                </Section>
                <Section title="Sync strategy">
                <p className="text-xs text-zinc-500 mb-3">Consistency &amp; deduplication</p>
                {selectedConsistency == null && (
                  <p className="text-xs text-amber-400 mb-2">Select a strategy (required)</p>
                )}
                <div className="space-y-1.5">
                  {consistencyOptions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setSelectedConsistency(c.id);
                        setShowWmSub(c.id === 'Incremental');
                      }}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                      selectedConsistency === c.id ? 'bg-indigo-500/20 ring-1 ring-indigo-500/30' : 'bg-zinc-800/70 hover:bg-zinc-700/80'
                    }`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center ${
                          selectedConsistency === c.id ? 'bg-indigo-500' : 'bg-white/20'
                        }`}
                      >
                        {selectedConsistency === c.id && <Check className="w-2.5 h-2.5 text-white" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white">{c.label}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{c.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
                {showWmSub && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    <div>
                      <label className="text-[11px] text-zinc-500 block mb-1.5">Watermark column</label>
                      <select
                        value={watermarkColumn}
                        onChange={(e) => setWatermarkColumn(e.target.value)}
                        className="px-3 py-2 text-xs rounded-lg bg-white/[0.06] text-white outline-none focus:bg-white/[0.08] min-w-[120px]"
                      >
                        <option value="updated_at">updated_at</option>
                        <option value="created_at">created_at</option>
                        <option value="last_modified">last_modified</option>
                        <option value="modified_at">modified_at</option>
                        <option value="synced_at">synced_at</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] text-zinc-500 block mb-1.5">Primary key</label>
                      <select
                        value={primaryKey}
                        onChange={(e) => setPrimaryKey(e.target.value)}
                        className="px-3 py-2 text-xs rounded-lg bg-white/[0.06] text-white outline-none focus:bg-white/[0.08] min-w-[120px]"
                      >
                        <option value="id">id</option>
                        <option value="customer_id">customer_id</option>
                        <option value="email">email</option>
                        <option value="uuid">uuid</option>
                        <option value="pk">pk</option>
                      </select>
                    </div>
                  </div>
                )}
                </Section>
              </div>
              <div className="space-y-10">
                <Section title="Destination">
                <p className="text-xs text-zinc-500 mb-3">Where to sync</p>
                {selectedDestination == null && (
                  <p className="text-xs text-amber-400 mb-2">Select a destination (required)</p>
                )}
                {selectedDestination != null && !hasDestinationTarget && (
                  <p className="text-xs text-amber-400 mb-2">
                    Select a {selectedDestination === 'Knowledge Base' ? 'knowledge base' : selectedDestination === 'Vector DB' ? 'vector store' : selectedDestination === 'Cache' ? 'cache' : 'data vault'}
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  {destinationOptions.map((d) => (
                    <div key={d.id}>
                      <button
                        type="button"
                        onClick={() => {
                          const next = selectedDestination === d.id ? null : d.id;
                          setSelectedDestination(next);
                          setSelectedKbId(null);
                          setSelectedVectorDbId(null);
                          setSelectedCacheId(null);
                          setSelectedDirectoryId(null);
                        }}
                        className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                          selectedDestination === d.id ? 'bg-indigo-500/20 ring-1 ring-indigo-500/30' : 'bg-zinc-800/70 hover:bg-zinc-700/80'
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center ${
                            selectedDestination === d.id ? 'bg-indigo-500' : 'bg-white/20'
                          }`}
                        >
                          {selectedDestination === d.id && <Check className="w-2.5 h-2.5 text-white" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-white">{d.label}</div>
                          <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{d.desc}</div>
                        </div>
                      </button>
                      {selectedDestination === d.id && (
                        <div className="mt-3">
                          <div
                            className={`rounded-lg overflow-hidden bg-zinc-800/50 max-h-[220px] overflow-y-auto ${d.id === 'Knowledge Base' ? 'min-h-[100px]' : ''}`}
                          >
                          {d.id === 'Knowledge Base' && (
                            <>
                              {kbOptionsError && (
                                <p className="text-xs text-amber-500 px-3 py-2">{kbOptionsError}</p>
                              )}
                              {kbOptionsLoading ? (
                                <p className="text-xs text-gray-500 px-3 py-2">Loading knowledge bases…</p>
                              ) : kbOptions.length === 0 ? (
                                <p className="text-xs text-gray-500 px-3 py-2">No knowledge bases found.</p>
                              ) : (
                                <>
                                  <div className="sticky top-0 z-10 bg-zinc-900/95 px-3 py-2">
                                    <ListFilter
                                      value={kbFilter}
                                      onChange={setKbFilter}
                                      placeholder="Search knowledge bases…"
                                    />
                                  </div>
                                  <div className="max-h-[180px] overflow-y-auto">
                                    {filteredKbOptions.map((kb) => (
                                      <button
                                        key={kb.id}
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setSelectedKbId(selectedKbId === kb.id ? null : kb.id);
                                        }}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-xs transition-colors ${
                                          selectedKbId === kb.id ? 'bg-indigo-500/20 text-white' : 'hover:bg-zinc-700/80 text-zinc-300'
                                        }`}
                                      >
                                        <span
                                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                            kb.status === 'active' ? 'bg-emerald-500' : 'bg-zinc-500'
                                          }`}
                                        />
                                        <span className="flex-1">{kb.name}</span>
                                        <span className="text-zinc-500">{kb.meta}</span>
                                        {selectedKbId === kb.id && <Check className="w-3.5 h-3.5 text-indigo-400" />}
                                      </button>
                                    ))}
                                  </div>
                                  {filteredKbOptions.length === 0 && kbOptions.length > 0 && (
                                    <p className="text-xs text-zinc-500 px-3 py-2">No knowledge bases match your filter.</p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                          {d.id === 'Vector DB' && (
                            <>
                              {vectorStoreError && (
                                <p className="text-xs text-amber-500 px-3 py-2">{vectorStoreError}</p>
                              )}
                              {vectorStoreLoading ? (
                                <p className="text-xs text-gray-500 px-3 py-2">Loading vector stores…</p>
                              ) : vectorStoreOptions.length === 0 ? (
                                <p className="text-xs text-gray-500 px-3 py-2">No vector stores found. Add one in Data Vault &gt; Vector Store.</p>
                              ) : (
                                <>
                                  <div className="sticky top-0 z-10 bg-zinc-900/95 px-3 py-2">
                                    <ListFilter
                                      value={vectorDbFilter}
                                      onChange={setVectorDbFilter}
                                      placeholder="Search vector stores…"
                                    />
                                  </div>
                                  <div className="max-h-[180px] overflow-y-auto">
                                    {filteredVectorStores.map((vd) => (
                                      <button
                                        key={vd.id}
                                        type="button"
                                        onClick={() => setSelectedVectorDbId(selectedVectorDbId === vd.id ? null : vd.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-xs transition-colors ${
                                          selectedVectorDbId === vd.id ? 'bg-indigo-500/15 text-white' : 'hover:bg-white/[0.06] text-zinc-300'
                                        }`}
                                      >
                                        <span className="flex-1 truncate">{vd.name}</span>
                                        <span className="text-zinc-500 truncate">{vd.meta}</span>
                                        {selectedVectorDbId === vd.id && <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />}
                                      </button>
                                    ))}
                                  </div>
                                  {filteredVectorStores.length === 0 && vectorStoreOptions.length > 0 && (
                                    <p className="text-xs text-zinc-500 px-3 py-2">No vector stores match your filter.</p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                          {d.id === 'Cache' && (
                            <>
                              {cacheError && (
                                <p className="text-xs text-amber-500 px-3 py-2">{cacheError}</p>
                              )}
                              {cacheLoading ? (
                                <p className="text-xs text-gray-500 px-3 py-2">Loading cache (Redis)…</p>
                              ) : cacheOptions.length === 0 ? (
                                <p className="text-xs text-gray-500 px-3 py-2">No cache found. Add one in Data Vault &gt; Cache.</p>
                              ) : (
                                <>
                                  <div className="sticky top-0 z-10 bg-zinc-900/95 px-3 py-2">
                                    <ListFilter
                                      value={cacheFilter}
                                      onChange={setCacheFilter}
                                      placeholder="Search cache (Redis)…"
                                    />
                                  </div>
                                  <div className="max-h-[180px] overflow-y-auto">
                                    {filteredCacheOptions.map((c) => (
                                      <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => setSelectedCacheId(selectedCacheId === c.id ? null : c.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-xs transition-colors ${
                                          selectedCacheId === c.id ? 'bg-indigo-500/20 text-white' : 'hover:bg-zinc-700/80 text-zinc-300'
                                        }`}
                                      >
                                        <span className="flex-1 truncate">{c.name}</span>
                                        <span className="text-zinc-500 truncate">{c.meta}</span>
                                        {selectedCacheId === c.id && <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />}
                                      </button>
                                    ))}
                                  </div>
                                  {filteredCacheOptions.length === 0 && cacheOptions.length > 0 && (
                                    <p className="text-xs text-zinc-500 px-3 py-2">No cache instances match your filter.</p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                          {d.id === 'Directory' && (
                            <>
                              {directoryError && (
                                <p className="text-xs text-amber-500 px-3 py-2">{directoryError}</p>
                              )}
                              {directoryLoading ? (
                                <p className="text-xs text-gray-500 px-3 py-2">Loading data vaults…</p>
                              ) : directoryOptions.length === 0 ? (
                                <p className="text-xs text-gray-500 px-3 py-2">No data vaults found. Add one in Data Vault &gt; Data Vault.</p>
                              ) : (
                                <>
                                  <div className="sticky top-0 z-10 bg-zinc-900/95 px-3 py-2">
                                    <ListFilter
                                      value={directoryFilter}
                                      onChange={setDirectoryFilter}
                                      placeholder="Search directories / data vaults…"
                                    />
                                  </div>
                                  <div className="max-h-[180px] overflow-y-auto">
                                    {filteredDirectoryOptions.map((dir) => (
                                      <button
                                        key={dir.id}
                                        type="button"
                                        onClick={() => setSelectedDirectoryId(selectedDirectoryId === dir.id ? null : dir.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-xs transition-colors ${
                                          selectedDirectoryId === dir.id ? 'bg-indigo-500/20 text-white' : 'hover:bg-zinc-700/80 text-zinc-300'
                                        }`}
                                      >
                                        <span className="flex-1 truncate">{dir.name}</span>
                                        <span className="text-zinc-500 truncate">{dir.meta}</span>
                                        {selectedDirectoryId === dir.id && <Check className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />}
                                      </button>
                                    ))}
                                  </div>
                                  {filteredDirectoryOptions.length === 0 && directoryOptions.length > 0 && (
                                    <p className="text-xs text-zinc-500 px-3 py-2">No directories match your filter.</p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                    ))}
                  </div>
                </Section>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-3 px-8 py-5 flex-shrink-0 bg-zinc-900/50 border-t border-zinc-800/80">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 px-5 text-sm font-medium rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors inline-flex items-center justify-center"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allRequiredFilled}
            className="flex-1 py-2.5 px-5 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="w-4 h-4" />
            Save Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditScheduleModal;
